import { useEffect } from 'react';
import { useLocation } from 'react-router';

export const APP_NAME = 'Cavalry HR';

// Auth route titles (used by AuthLayout)
const AUTH_PAGE_TITLES = {
  'sign-in': 'Sign In',
  'sign-up': 'Sign Up',
  // 'tony': 'Sign Up',
  'confirm-email': 'Confirm Email',
  'reset-password': 'Reset Password',
  'intro': 'Introduction',
  'apply-profile': 'Apply',
  'questionnaire': 'Questionnaire',
  'video-interview': 'Video Interview',
  'video-interview-intro': 'Video Interview',
  'start-company': 'Company',
};

/**
 * Set document title with consistent formatting
 */
const setTitle = (title) => {
  document.title = title ? `${title} • ${APP_NAME}` : APP_NAME;
};

/**
 * Get title from auth page route segment
 */
const getAuthPageTitle = (pathname) => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  return AUTH_PAGE_TITLES[lastSegment] || null;
};

/**
 * Generate title for job application pages
 */
const getJobApplicationTitle = (jobInfo, pageName) => {
  if (!jobInfo?.getJobInfo) return pageName;
  
  const { title: jobTitle, client, hideClientName } = jobInfo.getJobInfo;
  
  if (hideClientName || !client?.company) {
    return `${pageName} - ${jobTitle}`;
  }
  
  return `${pageName} - ${jobTitle} at ${client.company}`;
};

/**
 * Hook to set document.title from breadcrumbs (for NavBreadcrumbs)
 * @param {Array} breadcrumbs - Array of breadcrumb objects with { breadcrumb } property
 */
export const useDocumentTitleFromBreadcrumbs = (breadcrumbs) => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      document.title = APP_NAME;
      return;
    }

    if (breadcrumbs.length) {
      const last = breadcrumbs[breadcrumbs.length - 1];
      const raw = last?.breadcrumb;
      const titleCore = typeof raw === 'string' ? raw : String(raw ?? '');
      setTitle(titleCore);
    } else {
      document.title = APP_NAME;
    }
  }, [location.pathname, breadcrumbs]);
};

/**
 * Hook to set document.title for auth pages (for AuthLayout)
 * @param {Object} options
 * @param {Object} [options.jobInfo] - Job info from GraphQL query
 * @param {boolean} [options.isApply] - Whether this is an apply flow
 * @param {boolean} [options.skip] - Skip setting title (useful when loading)
 */
export const useDocumentTitleForAuth = ({ jobInfo, isApply = false, skip = false } = {}) => {
  const location = useLocation();

  useEffect(() => {
    if (skip) return;

    const baseTitle = getAuthPageTitle(location.pathname);
    
    if (!baseTitle) {
      document.title = APP_NAME;
      return;
    }

    if (isApply && jobInfo?.getJobInfo) {
      setTitle(getJobApplicationTitle(jobInfo, baseTitle));
    } else {
      setTitle(baseTitle);
    }
  }, [location.pathname, jobInfo, isApply, skip]);
};

/**
 * Generic hook to set document.title (for custom titles)
 * @param {string} title - Explicit title to set
 * @param {boolean} [skip] - Skip setting title
 */
export const useDocumentTitle = (title, skip = false) => {
  useEffect(() => {
    if (skip) return;
    setTitle(title);
  }, [title, skip]);
};

export default useDocumentTitle;
